import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  Loader2, Save, Search, BedDouble, ArrowLeft, Plus, X, RotateCcw, Trash2,
  CheckCircle, Power, ChevronDown, ChevronRight, Upload,
} from "lucide-react";
import AdminAuthGate, {
  adminHeaders, clearAdminPassword,
} from "@/components/admin/AdminAuthGate";
import { buildHotelRoomTree, slugifyRoom } from "@/lib/roomRegistry";
import { safeReadBody } from "@/lib/fetchWithRetry";

const API = process.env.REACT_APP_BACKEND_URL || "";
const LANGS = [
  { key: "en", label: "EN" },
  { key: "fr", label: "FR" },
  { key: "es", label: "ES" },
];

const pickStr = (v) => {
  if (!v) return "";
  if (typeof v === "string") return v;
  return v.en || v.fr || v.es || "";
};

// Convert any value to a multilingual object — used when seeding the form
const toML = (v) => (v && typeof v === "object" ? { en: v.en || "", fr: v.fr || "", es: v.es || "" } : { en: v || "", fr: "", es: "" });

// Clean an empty multilingual object back to undefined so we don't store noise
const compactML = (v) => {
  if (!v) return undefined;
  const en = (v.en || "").trim();
  const fr = (v.fr || "").trim();
  const es = (v.es || "").trim();
  if (!en && !fr && !es) return undefined;
  return { en, fr, es };
};

// ────────────────────────────────────────────────────────────────────────
// Tabs for multilingual inputs
// ────────────────────────────────────────────────────────────────────────
const MLField = ({ label, value, onChange, multiline, placeholder, testid }) => {
  const [lang, setLang] = useState("en");
  const v = value || { en: "", fr: "", es: "" };
  const Tag = multiline ? "textarea" : "input";
  return (
    <div data-testid={testid}>
      <div className="flex items-center justify-between">
        <label className="text-[10px] tracking-[0.25em] uppercase text-[#5C5248]">{label}</label>
        <div className="flex gap-1">
          {LANGS.map((l) => (
            <button
              key={l.key}
              type="button"
              onClick={() => setLang(l.key)}
              className={`text-[10px] tracking-[0.18em] uppercase px-2 py-1 transition-colors ${
                lang === l.key ? "bg-[#2C2621] text-[#FDFBF7]" : "text-[#5C5248] hover:text-[#C16542]"
              }`}
              data-testid={`${testid}-tab-${l.key}`}
            >
              {l.label}
            </button>
          ))}
        </div>
      </div>
      <Tag
        value={v[lang] || ""}
        onChange={(e) => onChange({ ...v, [lang]: e.target.value })}
        placeholder={placeholder}
        rows={multiline ? 4 : undefined}
        className="mt-2 w-full border border-[#2C2621]/15 px-3 py-2.5 text-sm focus:outline-none focus:border-[#C16542] bg-white"
        data-testid={`${testid}-input-${lang}`}
      />
    </div>
  );
};

const TextField = ({ label, value, onChange, placeholder, testid }) => (
  <div data-testid={testid}>
    <label className="text-[10px] tracking-[0.25em] uppercase text-[#5C5248]">{label}</label>
    <input
      value={value || ""}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className="mt-2 w-full border border-[#2C2621]/15 px-3 py-2.5 text-sm focus:outline-none focus:border-[#C16542] bg-white"
      data-testid={`${testid}-input`}
    />
  </div>
);

// Amenities tag editor
const AmenitiesEditor = ({ value, onChange }) => {
  const [draft, setDraft] = useState("");
  const list = value || [];
  return (
    <div data-testid="amenities-editor">
      <label className="text-[10px] tracking-[0.25em] uppercase text-[#5C5248]">Amenities</label>
      <div className="mt-2 flex flex-wrap gap-1.5">
        {list.map((a, i) => (
          <span
            key={`${a}-${i}`}
            className="inline-flex items-center gap-1.5 bg-[#F2EBE1] text-[#2C2621] px-2.5 py-1 text-xs"
            data-testid={`amenity-tag-${i}`}
          >
            {a}
            <button
              type="button"
              onClick={() => onChange(list.filter((_, idx) => idx !== i))}
              className="text-[#5C5248] hover:text-[#C16542]"
              aria-label={`Remove ${a}`}
              data-testid={`amenity-remove-${i}`}
            >
              <X className="w-3 h-3" />
            </button>
          </span>
        ))}
      </div>
      <form
        className="mt-2 flex gap-2"
        onSubmit={(e) => {
          e.preventDefault();
          const v = draft.trim();
          if (!v) return;
          onChange([...list, v]);
          setDraft("");
        }}
      >
        <input
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          placeholder="Add amenity (then press +)"
          className="flex-1 border border-[#2C2621]/15 px-3 py-2 text-sm focus:outline-none focus:border-[#C16542] bg-white"
          data-testid="amenity-input"
        />
        <button
          type="submit"
          className="inline-flex items-center gap-1.5 border border-[#2C2621]/20 hover:border-[#C16542] hover:text-[#C16542] px-3 py-2 text-[10px] tracking-[0.22em] uppercase transition-colors"
          data-testid="amenity-add"
        >
          <Plus className="w-3.5 h-3.5" /> Add
        </button>
      </form>
    </div>
  );
};

// Pricing editor — 4 board options, all values in MAD (Moroccan Dirham)
const PRICING_KEYS = [
  { key: "room_only",  label: "Room Only",         placeholder: "e.g. 850" },
  { key: "breakfast",  label: "Room + Breakfast",  placeholder: "e.g. 950" },
  { key: "half_board", label: "Half Board",        placeholder: "e.g. 1200" },
  { key: "full_board", label: "Full Board",        placeholder: "e.g. 1500" },
];

const PricingEditor = ({ value, onChange }) => {
  const v = value || {};
  const update = (key, raw) => {
    const trimmed = String(raw).trim();
    if (trimmed === "") {
      const next = { ...v };
      delete next[key];
      onChange(next);
      return;
    }
    const n = Number(trimmed);
    if (Number.isNaN(n) || n < 0) return;
    onChange({ ...v, [key]: n });
  };
  return (
    <div data-testid="pricing-editor">
      {/* Per-person pricing — the brand-canonical customer-facing figure */}
      <div className="flex items-center justify-between">
        <label className="text-[10px] tracking-[0.25em] uppercase text-[#C16542]">
          Per-person pricing (MAD · commercial rate)
        </label>
        <span className="text-[10px] tracking-[0.18em] uppercase text-[#5C5248]/70">
          Per person · per night · shown to guests
        </span>
      </div>
      <div className="mt-2 grid grid-cols-1 sm:grid-cols-[1fr_auto] gap-3 items-center">
        <label className="text-xs text-[#2C2621]">Room-only price per person · per night</label>
        <div className="relative">
          <input
            type="number"
            inputMode="numeric"
            min="0"
            step="10"
            value={v.price_per_person ?? ""}
            onChange={(e) => update("price_per_person", e.target.value)}
            placeholder="e.g. 425"
            className="w-32 border border-[#C16542]/40 px-3 py-2 pr-12 text-sm focus:outline-none focus:border-[#C16542] bg-white tabular-nums text-right"
            data-testid="pricing-input-price_per_person"
          />
          <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[10px] tracking-[0.18em] uppercase text-[#5C5248]/70 pointer-events-none">MAD</span>
        </div>
        <label className="text-xs text-[#2C2621]">
          Base occupancy used for the per-person figure
          <span className="ml-1 text-[#5C5248]/70">(auto from capacity if blank)</span>
        </label>
        <div className="relative">
          <input
            type="number"
            inputMode="numeric"
            min="1"
            step="1"
            value={v.occupancy ?? ""}
            onChange={(e) => update("occupancy", e.target.value)}
            placeholder="2"
            className="w-32 border border-[#2C2621]/15 px-3 py-2 pr-12 text-sm focus:outline-none focus:border-[#C16542] bg-white tabular-nums text-right"
            data-testid="pricing-input-occupancy"
          />
          <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[10px] tracking-[0.18em] uppercase text-[#5C5248]/70 pointer-events-none">pax</span>
        </div>
      </div>

      {/* Per-room prices — legacy CMS fields, still supported as an
          override for teams that quote whole-room rates. */}
      <div className="flex items-center justify-between mt-6">
        <label className="text-[10px] tracking-[0.25em] uppercase text-[#5C5248]">Per-room pricing (MAD · optional)</label>
        <span className="text-[10px] tracking-[0.18em] uppercase text-[#5C5248]/70">Per night · leave blank to hide</span>
      </div>
      <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-3">
        {PRICING_KEYS.map((opt) => (
          <div key={opt.key} className="flex items-center gap-2">
            <label className="flex-1 text-xs text-[#2C2621]">{opt.label}</label>
            <div className="relative">
              <input
                type="number"
                inputMode="numeric"
                min="0"
                step="50"
                value={v[opt.key] ?? ""}
                onChange={(e) => update(opt.key, e.target.value)}
                placeholder={opt.placeholder}
                className="w-32 border border-[#2C2621]/15 px-3 py-2 pr-12 text-sm focus:outline-none focus:border-[#C16542] bg-white tabular-nums text-right"
                data-testid={`pricing-input-${opt.key}`}
              />
              <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[10px] tracking-[0.18em] uppercase text-[#5C5248]/70 pointer-events-none">MAD</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// ────────────────────────────────────────────────────────────────────────
// Room editor pane
// ────────────────────────────────────────────────────────────────────────
const buildFormFromBase = (room, override) => {
  const fields = (override && override.fields) || {};
  const baseSurface = typeof room.surface === "string" ? room.surface : pickStr(room.surface);
  return {
    name: toML(fields.name ?? room.name),
    number: fields.number ?? "",
    type: fields.type ?? "",
    category: fields.category ?? "",
    desc: toML(fields.desc ?? room.desc),
    surface: fields.surface != null ? (typeof fields.surface === "string" ? fields.surface : pickStr(fields.surface)) : baseSurface,
    capacity: toML(fields.capacity ?? room.capacity),
    amenities: Array.isArray(fields.amenities) ? fields.amenities : (room.amenities || []),
    cover: fields.cover ?? room.image ?? "",
    pricing: (fields.pricing && typeof fields.pricing === "object") ? fields.pricing : {},
    is_active: override ? override.is_active !== false : true,
    is_new: !!(override && override.is_new),
  };
};

const RoomEditor = ({ hotel, room, override, onSaved, onReset, onDeleted }) => {
  const [form, setForm] = useState(() => buildFormFromBase(room, override));
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [busyAction, setBusyAction] = useState("");

  useEffect(() => {
    setForm(buildFormFromBase(room, override));
    setSuccess("");
    setError("");
  }, [hotel?.slug, room?._slug, override]);

  const onField = (key, value) => setForm((f) => ({ ...f, [key]: value }));

  const onCoverUpload = async (e) => {
    const file = e.target.files && e.target.files[0];
    e.target.value = "";
    if (!file) return;
    setUploading(true);
    setError("");
    setSuccess("");
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await fetch(
        `${API}/api/admin/hotels/${hotel.slug}/rooms/${room._slug}/cover-upload`,
        { method: "POST", headers: adminHeaders(), body: fd }
      );
      if (!res.ok) {
        const msg = await safeReadBody(res);
        throw new Error(msg ? `Upload failed: ${msg.slice(0, 200)}` : `Upload failed (HTTP ${res.status})`);
      }
      const data = await res.json();
      setForm((f) => ({ ...f, cover: data.url }));
      setSuccess("Cover uploaded. Click Save to publish.");
    } catch (err) {
      setError(err.message);
    } finally {
      setUploading(false);
    }
  };

  const save = async () => {
    setSaving(true);
    setError("");
    setSuccess("");
    try {
      // Strip empty pricing values; if all are empty, send undefined so the
      // backend doesn't persist an empty object.
      const pricingEntries = Object.entries(form.pricing || {}).filter(
        ([, v]) => v !== null && v !== undefined && v !== "" && !Number.isNaN(Number(v))
      ).map(([k, v]) => [k, Number(v)]);
      const pricingPayload = pricingEntries.length ? Object.fromEntries(pricingEntries) : undefined;
      const payload = {
        is_active: !!form.is_active,
        is_new: !!form.is_new,
        fields: {
          name: compactML(form.name),
          number: (form.number || "").trim() || undefined,
          type: (form.type || "").trim() || undefined,
          category: (form.category || "").trim() || undefined,
          desc: compactML(form.desc),
          surface: (form.surface || "").trim() || undefined,
          capacity: compactML(form.capacity),
          amenities: form.amenities && form.amenities.length ? form.amenities : undefined,
          cover: (form.cover || "").trim() || undefined,
          pricing: pricingPayload,
        },
      };
      const res = await fetch(`${API}/api/admin/hotels/${hotel.slug}/rooms/${room._slug}`, {
      credentials: "include",
        method: "PUT",
        headers: { ...adminHeaders(), "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error(`Save failed (${res.status})`);
      const data = await res.json();
      setSuccess("Changes saved. Public pages updated.");
      onSaved && onSaved(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const reset = async () => {
    if (!override) return;
    if (!window.confirm(form.is_new
      ? "Delete this CMS-only room permanently?"
      : "Reset all overrides for this room? It will revert to the static defaults.")) return;
    try {
      const res = await fetch(`${API}/api/admin/hotels/${hotel.slug}/rooms/${room._slug}`, {
      credentials: "include",
        method: "DELETE",
        headers: adminHeaders(),
      });
      if (!res.ok) throw new Error(`Reset failed (${res.status})`);
      onReset && onReset();
    } catch (err) {
      setError(err.message);
    }
  };

  // Permanently delete the room category. CMS-only rooms are hard-removed
  // from MongoDB; base rooms are replaced with a tombstone (is_deleted=true,
  // fields cleared). Deletion is one-way — there is no restore.
  const deleteCategory = async () => {
    const isCmsOnly = !!form.is_new;
    const msg = isCmsOnly
      ? "Permanently delete this CMS-only room?\n\nThis erases the room from the database and cannot be undone."
      : "Permanently delete this room?\n\nThe room will be removed from the database and will no longer appear anywhere on the website, in the admin panel, or in any room selector. This cannot be undone.";
    if (!window.confirm(msg)) return;
    setBusyAction("delete");
    setError("");
    setSuccess("");
    try {
      const res = await fetch(
        `${API}/api/admin/hotels/${hotel.slug}/rooms/${room._slug}/delete`,
        { method: "POST", headers: adminHeaders() }
      );
      if (!res.ok) {
        const body = await safeReadBody(res);
        throw new Error(body ? `Delete failed: ${body.slice(0, 200)}` : `Delete failed (HTTP ${res.status})`);
      }
      const data = await res.json();
      onDeleted && onDeleted({ mode: data.mode || (isCmsOnly ? "hard" : "tombstone") });
    } catch (err) {
      setError(err.message);
    } finally {
      setBusyAction("");
    }
  };

  return (
    <div className="flex-1 min-w-0" data-testid="room-editor">
      <header className="px-6 md:px-10 py-7 border-b border-[#2C2621]/10 bg-white sticky top-0 z-10">
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div className="min-w-0">
            <p className="overline text-[#C16542] flex items-center gap-2 flex-wrap">
              <BedDouble className="w-3.5 h-3.5" strokeWidth={1.6} />
              {hotel.name}
              {form.is_new && (
                <span className="inline-flex items-center gap-1 bg-[#2C2621] text-[#FDFBF7] px-2 py-0.5 text-[9px] tracking-[0.18em] uppercase">
                  CMS-only
                </span>
              )}
            </p>
            <h2 className="font-serif-x text-3xl md:text-4xl mt-2 tracking-tight" data-testid="room-editor-title">
              {pickStr(form.name) || pickStr(room.name) || "Unnamed room"}
            </h2>
            <p className="mt-1 text-[10px] tracking-[0.25em] uppercase text-[#5C5248]">
              {form.number ? `№ ${form.number}` : "no room number"}
              {form.type ? ` · ${form.type}` : ""}
              {form.category ? ` · ${form.category}` : ""}
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <label className="inline-flex items-center gap-2 text-[11px] tracking-[0.18em] uppercase text-[#5C5248] cursor-pointer">
              <Power className={`w-3.5 h-3.5 ${form.is_active ? "text-[#5A6B4F]" : "text-[#C16542]"}`} strokeWidth={1.8} />
              <input
                type="checkbox"
                checked={!!form.is_active}
                onChange={(e) => onField("is_active", e.target.checked)}
                className="sr-only"
                data-testid="room-active-toggle"
              />
              <span className={`relative inline-block w-10 h-5 transition-colors ${form.is_active ? "bg-[#5A6B4F]" : "bg-[#5C5248]/30"}`}>
                <span className={`absolute top-0.5 ${form.is_active ? "left-5" : "left-0.5"} w-4 h-4 bg-white transition-all`} />
              </span>
              {form.is_active ? "Active" : "Inactive"}
            </label>
            <button
              type="button"
              onClick={save}
              disabled={saving}
              className="inline-flex items-center gap-2 bg-[#C16542] hover:bg-[#A35133] disabled:opacity-50 text-[#FDFBF7] px-5 py-3 text-[11px] tracking-[0.25em] uppercase transition-colors"
              data-testid="room-save"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" strokeWidth={1.6} />}
              Save
            </button>
            {override && !form.is_new && (
              <button
                type="button"
                onClick={reset}
                className="inline-flex items-center gap-2 border border-[#2C2621]/20 hover:border-[#C16542] hover:text-[#C16542] px-4 py-3 text-[11px] tracking-[0.25em] uppercase transition-colors"
                data-testid="room-reset"
              >
                <RotateCcw className="w-3.5 h-3.5" /> Reset
              </button>
            )}
            <button
              type="button"
              onClick={deleteCategory}
              disabled={busyAction === "delete"}
              className="inline-flex items-center gap-2 border border-[#C16542]/40 text-[#C16542] hover:bg-[#C16542] hover:text-[#FDFBF7] disabled:opacity-50 px-4 py-3 text-[11px] tracking-[0.25em] uppercase transition-colors"
              data-testid="room-delete"
            >
              {busyAction === "delete" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
              Delete permanently
            </button>
          </div>
        </div>
        {error && <p className="mt-3 text-sm text-[#C16542]" data-testid="room-editor-error">{error}</p>}
        {success && !error && (
          <p className="mt-3 inline-flex items-center gap-1.5 text-sm text-[#5A6B4F]" data-testid="room-editor-success">
            <CheckCircle className="w-4 h-4" strokeWidth={1.8} /> {success}
          </p>
        )}
      </header>

      <div className="px-6 md:px-10 py-8 space-y-7 max-w-3xl">
        <MLField label="Room name" value={form.name} onChange={(v) => onField("name", v)} placeholder="e.g. Standard Twin Room" testid="field-name" />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          <TextField label="Room number" value={form.number} onChange={(v) => onField("number", v)} placeholder="101, 102…" testid="field-number" />
          <TextField label="Room type" value={form.type} onChange={(v) => onField("type", v)} placeholder="Standard, Suite, Family…" testid="field-type" />
          <TextField label="Category" value={form.category} onChange={(v) => onField("category", v)} placeholder="Garden, Pool view…" testid="field-category" />
        </div>
        <MLField label="Description" value={form.desc} onChange={(v) => onField("desc", v)} multiline placeholder="Room story, materials, view, ambience…" testid="field-desc" />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <TextField label="Surface" value={form.surface} onChange={(v) => onField("surface", v)} placeholder="24 m² · single occupancy" testid="field-surface" />
          <MLField label="Capacity" value={form.capacity} onChange={(v) => onField("capacity", v)} placeholder="1 voyageur" testid="field-capacity" />
        </div>
        <AmenitiesEditor value={form.amenities} onChange={(v) => onField("amenities", v)} />

        <PricingEditor value={form.pricing} onChange={(v) => onField("pricing", v)} />

        {/* Cover image — URL paste OR direct upload */}
        <div data-testid="field-cover">
          <div className="flex items-center justify-between">
            <label className="text-[10px] tracking-[0.25em] uppercase text-[#5C5248]">Cover image</label>
            <label
              className="inline-flex items-center gap-1.5 border border-[#2C2621]/20 hover:border-[#C16542] hover:text-[#C16542] px-3 py-1.5 text-[10px] tracking-[0.22em] uppercase cursor-pointer transition-colors"
              data-testid="cover-upload-trigger"
            >
              {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" strokeWidth={1.6} />}
              {uploading ? "Uploading…" : "Upload"}
              <input
                type="file"
                accept="image/png,image/jpeg,image/webp,image/avif,image/gif"
                className="hidden"
                onChange={onCoverUpload}
                disabled={uploading}
                data-testid="cover-upload-input"
              />
            </label>
          </div>
          <input
            value={form.cover || ""}
            onChange={(e) => onField("cover", e.target.value)}
            placeholder="https://…  or click Upload to choose a file"
            className="mt-2 w-full border border-[#2C2621]/15 px-3 py-2.5 text-sm focus:outline-none focus:border-[#C16542] bg-white"
            data-testid="field-cover-input"
          />
        </div>
        {form.cover && (
          <div className="aspect-[16/10] overflow-hidden border border-[#2C2621]/10 bg-[#F2EBE1]" data-testid="cover-preview">
            <img
              src={form.cover.startsWith("/api/") ? `${API}${form.cover}` : form.cover}
              alt="cover preview"
              className="w-full h-full object-cover"
              loading="lazy"
            />
          </div>
        )}
        <div className="border-t border-[#2C2621]/10 pt-6">
          <Link
            to="/admin/galleries"
            className="inline-flex items-center gap-2 border border-[#2C2621]/20 hover:border-[#C16542] hover:text-[#C16542] px-4 py-3 text-[11px] tracking-[0.25em] uppercase transition-colors"
            data-testid="open-galleries"
          >
            Manage room photo gallery →
          </Link>
        </div>
      </div>
    </div>
  );
};

// ────────────────────────────────────────────────────────────────────────
// Page wrapper + sidebar
// ────────────────────────────────────────────────────────────────────────
const AdminHotelsInner = () => {
  const [tree] = useState(() => buildHotelRoomTree());
  const [overrides, setOverrides] = useState({}); // hotel_slug → { rooms: { room_slug: ov } }
  const [selected, setSelected] = useState(null); // {hotel, room (merged)}
  const [filter, setFilter] = useState("");
  const [expanded, setExpanded] = useState(() => new Set());

  // Load all overrides up-front
  const loadOverrides = useCallback(() => {
    fetch(`${API}/api/admin/hotels/overrides`, {
      credentials: "include", headers: adminHeaders() })
      .then((r) => r.json())
      .then((data) => {
        const grouped = {};
        for (const item of data.items || []) {
          if (!grouped[item.hotel_slug]) grouped[item.hotel_slug] = {};
          grouped[item.hotel_slug][item.room_slug] = item;
        }
        setOverrides(grouped);
      })
      .catch(() => {});
  }, []);
  useEffect(() => { loadOverrides(); }, [loadOverrides]);

  // Build the sidebar with overrides decoration. Permanently-deleted rooms
  // (tombstones) are filtered out completely — they must not appear in
  // the admin tree, the editor, room selectors, or anywhere else.
  const filtered = useMemo(() => {
    const isDead = (ov) => !!(ov && (ov.is_deleted || ov.is_hidden));
    return tree
      .map((node) => {
        const ovRooms = overrides[node.hotel.slug] || {};
        const cmsOnly = [];
        for (const [slug, ov] of Object.entries(ovRooms)) {
          if (!ov.is_new) continue;
          if (isDead(ov)) continue;
          if (node.rooms.some((r) => r.slug === slug)) continue;
          cmsOnly.push({ slug, name: { en: slug, fr: slug, es: slug }, isCmsOnly: true });
        }
        // Remove deleted base rooms from the sidebar entirely.
        const liveBase = node.rooms.filter((r) => !isDead(ovRooms[r.slug]));
        const rooms = [...liveBase, ...cmsOnly];
        if (!filter) return { ...node, rooms };
        const f = filter.toLowerCase();
        const filteredRooms = rooms.filter((r) =>
          (pickStr(r.name) + " " + node.hotel.name).toLowerCase().includes(f)
        );
        if (!filteredRooms.length) return null;
        return { ...node, rooms: filteredRooms };
      })
      .filter(Boolean);
  }, [tree, overrides, filter]);

  const fetchSelectedOverride = useCallback(async (hotelSlug, roomSlug) => {
    try {
      const res = await fetch(`${API}/api/admin/hotels/${hotelSlug}/rooms/${roomSlug}`, {
      credentials: "include", headers: adminHeaders() });
      if (!res.ok) return null;
      const data = await res.json();
      return data.exists ? data : null;
    } catch { return null; }
  }, []);

  // When selecting a room from the tree
  const selectRoom = async (hotel, room) => {
    setSelected({ hotel, room: { ...room, _slug: room.slug }, override: null, loading: true });
    const ov = await fetchSelectedOverride(hotel.slug, room.slug);
    setSelected({ hotel, room: { ...room, _slug: room.slug }, override: ov, loading: false });
  };

  // Add new room shortcut
  const onAddRoom = async (hotel) => {
    const baseName = window.prompt("Room name (EN):");
    if (!baseName) return;
    try {
      const res = await fetch(`${API}/api/admin/hotels/${hotel.slug}/rooms`, {
      credentials: "include",
        method: "POST",
        headers: { ...adminHeaders(), "Content-Type": "application/json" },
        body: JSON.stringify({
          is_active: true,
          is_new: true,
          fields: { name: { en: baseName, fr: baseName, es: baseName } },
        }),
      });
      if (!res.ok) throw new Error(`Create failed (${res.status})`);
      const data = await res.json();
      await loadOverrides();
      const newRoom = { slug: data.room_slug, name: data.fields.name, isCmsOnly: true };
      selectRoom(hotel, newRoom);
    } catch (err) { window.alert(err.message); }
  };

  const onSaved = async () => {
    await loadOverrides();
    if (selected) {
      const ov = await fetchSelectedOverride(selected.hotel.slug, selected.room._slug);
      setSelected((s) => ({ ...s, override: ov }));
    }
  };

  const onReset = async () => {
    await loadOverrides();
    if (selected) {
      // Removed override: revert to base room data
      if (selected.room.isCmsOnly) {
        setSelected(null);
      } else {
        setSelected((s) => ({ ...s, override: null }));
      }
    }
  };

  // After deleting a room category:
  // Permanent deletion handler — both CMS-only hard deletes and base-room
  // tombstones drop the room from the sidebar entirely, so we always clear
  // the selection.
  const onDeleted = async () => {
    await loadOverrides();
    setSelected(null);
  };

  const toggleHotel = (slug) => {
    setExpanded((e) => {
      const next = new Set(e);
      if (next.has(slug)) next.delete(slug);
      else next.add(slug);
      return next;
    });
  };

  return (
    <section className="bg-[#FDFBF7] min-h-screen" data-testid="admin-hotels-page">
      <header className="px-6 md:px-10 py-7 border-b border-[#2C2621]/10 bg-white">
        <div className="max-w-[1600px] mx-auto flex flex-wrap items-end justify-between gap-4">
          <div>
            <Link to="/admin" className="overline text-[#C16542] flex items-center gap-1.5 hover:underline" data-testid="admin-hotels-back">
              <ArrowLeft className="w-3 h-3" strokeWidth={2} /> Admin · Hotels & Camps
            </Link>
            <h1 className="font-serif-x text-4xl md:text-5xl mt-2 tracking-tight">
              Hotels & Camps Management
            </h1>
            <p className="mt-2 text-sm text-[#5C5248] max-w-2xl">
              Edit every accommodation listing across the group. Saved changes go live on the public site instantly — no code changes needed.
            </p>
          </div>
          <button
            type="button"
            onClick={() => { clearAdminPassword(); window.location.href = "/admin"; }}
            className="inline-flex items-center gap-2 border border-[#2C2621]/20 hover:border-[#C16542] hover:text-[#C16542] px-4 py-2.5 text-[10px] tracking-[0.25em] uppercase transition-colors"
            data-testid="admin-hotels-logout"
          >
            Lock
          </button>
        </div>
      </header>

      <div className="max-w-[1600px] mx-auto flex flex-col lg:flex-row min-h-[calc(100vh-150px)]">
        {/* SIDEBAR */}
        <aside className="lg:w-[380px] xl:w-[420px] border-b lg:border-b-0 lg:border-r border-[#2C2621]/10 bg-white" data-testid="admin-hotels-sidebar">
          <div className="px-5 py-5 border-b border-[#2C2621]/10">
            <label className="relative block">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#5C5248]" strokeWidth={1.6} />
              <input
                type="search"
                placeholder="Search hotels and rooms…"
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                className="w-full pl-10 pr-3 py-2.5 text-sm border border-[#2C2621]/15 focus:outline-none focus:border-[#C16542]"
                data-testid="admin-hotels-search"
              />
            </label>
          </div>
          <nav className="max-h-[calc(100vh-250px)] overflow-y-auto" data-testid="admin-hotels-tree">
            {filtered.map((node) => {
              const isOpen = expanded.has(node.hotel.slug) || !!filter;
              return (
                <div key={node.hotel.slug} className="border-b border-[#2C2621]/5">
                  <button
                    type="button"
                    onClick={() => toggleHotel(node.hotel.slug)}
                    className="w-full text-left px-5 py-4 flex items-center justify-between gap-3 hover:bg-[#F2EBE1]/50 transition-colors"
                    data-testid={`hotel-row-${node.hotel.slug}`}
                  >
                    <div className="min-w-0">
                      <p className="font-serif-x text-lg leading-tight text-[#2C2621] truncate">
                        {node.hotel.name}
                      </p>
                      <p className="mt-0.5 text-[10px] tracking-[0.22em] uppercase text-[#5C5248]">
                        {node.rooms.length} rooms
                        {overrides[node.hotel.slug] && (
                          <span className="ml-2 text-[#C16542]">
                            · {Object.keys(overrides[node.hotel.slug]).length} edited
                          </span>
                        )}
                      </p>
                    </div>
                    {isOpen ? <ChevronDown className="w-4 h-4 text-[#5C5248]" /> : <ChevronRight className="w-4 h-4 text-[#5C5248]" />}
                  </button>
                  {isOpen && (
                    <div className="pb-3">
                      <ul className="space-y-0.5">
                        {node.rooms.map((room) => {
                          const ov = overrides[node.hotel.slug]?.[room.slug];
                          const active =
                            selected &&
                            selected.hotel.slug === node.hotel.slug &&
                            selected.room._slug === room.slug;
                          return (
                            <li key={room.slug}>
                              <button
                                type="button"
                                onClick={() => selectRoom(node.hotel, room)}
                                className={`w-full text-left flex items-center justify-between gap-3 pl-7 pr-4 py-2 text-sm transition-colors ${
                                  active
                                    ? "bg-[#2C2621] text-[#FDFBF7]"
                                    : "text-[#2C2621] hover:bg-[#F2EBE1]/60"
                                }`}
                                data-testid={`room-row-${node.hotel.slug}-${room.slug}`}
                              >
                                <span className="truncate">{pickStr(room.name)}</span>
                                <span className="flex gap-1 shrink-0">
                                  {ov && ov.is_new && (
                                    <span className={`text-[9px] tracking-[0.18em] uppercase ${active ? "text-[#D4A373]" : "text-[#C16542]"}`}>NEW</span>
                                  )}
                                  {ov && !ov.is_new && (
                                    <span className={`text-[9px] tracking-[0.18em] uppercase ${active ? "text-[#D4A373]" : "text-[#A07042]"}`}>EDITED</span>
                                  )}
                                  {ov && ov.is_active === false && (
                                    <span className={`text-[9px] tracking-[0.18em] uppercase ${active ? "text-[#D4A373]" : "text-[#5C5248]"}`}>OFF</span>
                                  )}
                                </span>
                              </button>
                            </li>
                          );
                        })}
                      </ul>
                      <button
                        type="button"
                        onClick={() => onAddRoom(node.hotel)}
                        className="ml-7 mt-3 inline-flex items-center gap-1.5 text-[10px] tracking-[0.25em] uppercase text-[#C16542] hover:underline"
                        data-testid={`add-room-${node.hotel.slug}`}
                      >
                        <Plus className="w-3 h-3" /> Add room
                      </button>
                    </div>
                  )}
                </div>
              );
            })}
          </nav>
        </aside>

        {/* EDITOR */}
        {selected ? (
          selected.loading ? (
            <div className="flex-1 flex items-center justify-center py-32 text-[#5C5248]">
              <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading room…
            </div>
          ) : (
            <RoomEditor
              hotel={selected.hotel}
              room={selected.room}
              override={selected.override}
              onSaved={onSaved}
              onReset={onReset}
              onDeleted={onDeleted}
            />
          )
        ) : (
          <div className="flex-1 flex flex-col items-center justify-center text-[#5C5248] py-32 px-6 text-center">
            <BedDouble className="w-10 h-10 mb-4" strokeWidth={1.4} />
            <p className="font-serif-x text-2xl text-[#2C2621]">Pick a room to edit</p>
            <p className="mt-2 text-sm max-w-md">
              Expand a hotel on the left, pick a room, then edit its name, number, type, description, amenities, pricing (in MAD) and cover photo. Changes go live instantly.
            </p>
          </div>
        )}
      </div>
    </section>
  );
};

const AdminHotelsPage = () => (
  <AdminAuthGate>
    <AdminHotelsInner />
  </AdminAuthGate>
);

export { slugifyRoom };
export default AdminHotelsPage;
